<template>
    <AddFileSection
        :id="AddFileFormSectionIds.CLIENTS"
        :sectionHeading="fileClientStore.userAddedClients.size ? 'Client information' : ''"
    >
        <div class="clients-container">
            <!-- visually hide clients added by a third party (e.g. a TPS) -->
            <ClientFormCard
                v-for="({ mapKey }, arrayIdx) in tpsClientInfoArray"
                v-model:client="tpsClientInfoArray[arrayIdx].client"
                :key="`add-file-client-form-hidden-${mapKey}`"
                class="d-none"
                :formIndex="Number(mapKey)"
                :isTpsOrder="!!addFileStore.tpsOrder"
            />
            <ClientFormCard
                v-for="({ mapKey }, arrayIdx) in userAddedClientInfoArray"
                v-model:client="userAddedClientInfoArray[arrayIdx].client"
                :key="`add-file-client-form-${mapKey}`"
                class="mt-3"
                :formIndex="Number(mapKey)"
                :isTpsOrder="!!addFileStore.tpsOrder"
                @removeClient="removeClient(mapKey)"
            />
        </div>
        <DsButton
            v-if="fileClientStore.userAddedClients.size"
            :disabled="addFileStore.isFormDisabled"
            leading="far fa-plus-circle"
            size="sm"
            variant="secondary"
            @click="addClient"
        >
            Add client
        </DsButton>
        <ClientsSectionEmptyState
            v-else
            @addClient="addClient"
        />
    </AddFileSection>
</template>

<script setup lang="ts">
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds, type AddFileClient } from '@/company-portal/add-file/add-file.types';
import type { ClientInfoFormOptions } from '@/company-portal/add-file/use-client-info-forms';
import { useClientInfoForms } from '@/company-portal/add-file/use-client-info-forms';
import DsButton from '@/components/design-system/button/DsButton.vue';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import ClientFormCard from '@/components/Views/Company/AddFile/ClientFormCard.vue';
import ClientsSectionEmptyState from '@/components/Views/Company/AddFile/ClientsSectionEmptyState.vue';
import { ClientTypeEnum } from '@/components/Views/Company/File/file.types';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { computed, watchEffect } from 'vue';

const props = defineProps<{
    minVisibleClientInfoForms: ClientInfoFormOptions['minVisibleClientInfoForms'];
}>();

const addFileStore = useAddFileStore();
const fileClientStore = useFileClientStore();

const tpsClients = computed<AddFileClient[]>(() => (addFileStore.tpsOrder?.clients ?? []).filter(client => !!client.email));

const tpsClientCount = computed(() => tpsClients.value.length);

watchEffect(() => {
    fileClientStore.tpsClients.clear();
    tpsClients.value.forEach((client, idx) => {
        fileClientStore.tpsClients.set(String(idx), { ...client, role: client.role ?? ClientTypeEnum.OTHER });
    });
});

const { addClient, removeClient } = useClientInfoForms({
    minVisibleClientInfoForms: props.minVisibleClientInfoForms,
    hiddenClientInfoFormsCount: tpsClients.value.length,
});

const tpsClientInfoArray = computed(() => Array.from(fileClientStore.tpsClients, ([mapKey, client]) => ({ mapKey, client })));

const userAddedClientInfoArray = computed(() =>
    Array.from(fileClientStore.userAddedClients, ([mapKey, client]) => ({ mapKey, client })),
);
</script>

<style scoped>
/**
 * If we're in the Company Portal (no existing clients), start the count at "1"
 * If we're in TPS, start the count at number of existing clients + 1
 * (for example, if we have 3 existing TPS clients => start count at "Client 4")
 */
.clients-container {
    counter-reset: client-heading v-bind(tpsClientCount);
}
</style>
